import {
  AutocompleteInputChangeReason,
  Box,
  Chip,
  SxProps,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { RiskLevel } from "../../../../../features/customer/types/queries";
import {
  CustomerOrganization,
  CustomerOrgCompany,
} from "../../../../types/apiTypes";
import { NameIdOption } from "../../../../types/commonTypes";
import { Nullable } from "../../../../types/nullable";
import AutocompleteGroupedList from "../../AutocompleteGroupedList/AutocompleteGroupedList";
import LoadingSpinner from "../../loadingSpinner/LoadingSpinner";
import { salesPermitProps } from "./common";
import { AutocompleteBaseSingle } from "../../fields/autocompleteBase/AutocompleteBaseSingle";
import { useTranslation } from "react-i18next";

type Props = {
  options: CustomerOrgCompany[];
  value: Nullable<CustomerOrganization>;
  onInputChange: (value: string, reason: AutocompleteInputChangeReason) => void;
  onChange: (value: Nullable<CustomerOrganization>) => void;
  disableSalesPermissions?: boolean;
  isLoading: boolean;
  sx?: SxProps;
  disabled?: boolean;
  /**
   * When true, `options` already contains the full, fixed list of
   * organizations, so the component filters it itself as the user types
   * instead of assuming `options` are pre-filtered search results.
   */
  useFixedOptions?: boolean;
};

const CustomerOrgCompanyAutocompleteSingle = ({
  options,
  value,
  onInputChange,
  onChange,
  disableSalesPermissions = false,
  isLoading,
  sx,
  disabled = false,
  useFixedOptions = false,
}: Props) => {
  const { t } = useTranslation("common");
  const formattedOptions: NameIdOption[] = useMemo(() => {
    return options.flatMap((company) =>
      company.customerOrganizations.map((org: CustomerOrganization) => ({
        name: org.name,
        id: org.id,
      })),
    );
  }, [options]);

  const filterOptions = useFixedOptions
    ? undefined
    : (options: NameIdOption[]) => options;

  return (
    <AutocompleteBaseSingle
      options={formattedOptions}
      value={value}
      loadingText={<LoadingSpinner />}
      loading={isLoading}
      onChange={(value) => onChange(value)}
      onInputChange={(_, value, reason) => onInputChange(value, reason)}
      getOptionLabel={(option) => option.name}
      groupBy={(option) => {
        const company = options.find((company) =>
          company.customerOrganizations.some((org) => org.id === option.id),
        );
        return company ? company.name : "";
      }}
      filterOptions={filterOptions}
      placeholder={isLoading ? "" : t("common:writeToSearch")}
      renderGroup={({ group, children }) => (
        <AutocompleteGroupedList group={group} children={children} />
      )}
      renderOption={(props, option) => {
        const customerOrg = options
          .flatMap((company) => company.customerOrganizations)
          .find((org) => org.id === option.id);

        const riskLevel = customerOrg?.salesPermission?.riskLevel;
        const showChip =
          !disableSalesPermissions &&
          riskLevel &&
          riskLevel !== RiskLevel.GREEN;

        return (
          <li {...props}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography>{option.name}</Typography>
              {showChip && (
                <Chip
                  variant="outlined"
                  sx={{ fontWeight: 600, ml: 1 }}
                  {...salesPermitProps[
                    riskLevel as keyof typeof salesPermitProps
                  ]}
                />
              )}
            </Box>
          </li>
        );
      }}
      sx={sx}
      disabled={disabled}
    />
  );
};

export default CustomerOrgCompanyAutocompleteSingle;
